// 通用广告位管理页(按 slotType 参数化)
//
// 覆盖三个广告位:forum_list_icon / forum_list_card / article_banner
// spec: docs/superpowers/specs/2026-06-29-agent-ads-design.md §管理页
//
// order_mode: 后端 ListAds 返回 {order_mode, ads}。
// 本页 orderMode 使用本地 state,首次收到服务端数据时初始化(initialized buffer 模式)。
// 用户切换后立即 PUT slot-mode 保存;刷新后从服务端 order_mode 正确回显。

import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Loader2,
  PlusCircle,
  Trash2,
} from 'lucide-react'
import { Spinner, useToast } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { AD_CAP, AD_SLOT_LABEL, AD_SLOGAN_MAX } from '../_lib/ad-slot'
import type { AdSlotType } from '../_lib/ad-slot'
import {
  useAds,
  useCreateAd,
  useUpdateAd,
  useDeleteAd,
  useReorderAds,
  useSetSlotMode,
} from '../_lib/agent-ads-api'
import { uploadAvatarSmall } from '../_lib/upload-avatar'

/** 码点计数(支持 emoji / 代理对)。超限不截断输入,仅计数器变红 + 禁止提交 */
function sloganLen(value: string): number {
  return [...value].length
}

// article_banner 额外提示
const BANNER_HINT =
  '位 1-5 展示于文章顶部,位 6-10 展示于文章底部。'

export function AgentAdsSlotPage({ slotType }: { slotType: AdSlotType }) {
  const toast = useToast()
  const { data: rawAds, isLoading } = useAds(slotType)

  const createAd = useCreateAd()
  const updateAd = useUpdateAd()
  const deleteAd = useDeleteAd()
  const reorderAds = useReorderAds()
  const setSlotMode = useSetSlotMode()

  // order_mode 本地 state:初始值从服务端 order_mode 字段读取(initialized buffer 模式)
  const [orderMode, setOrderMode] = useState<'fixed' | 'random'>('random')
  const orderModeInitialized = useRef(false)
  useEffect(() => {
    if (rawAds && !orderModeInitialized.current) {
      orderModeInitialized.current = true
      setOrderMode(rawAds.order_mode)
    }
  }, [rawAds])

  // 行内 URL 编辑
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editUrl, setEditUrl] = useState('')

  // 行内 slogan 编辑
  const [editingSloganId, setEditingSloganId] = useState<string | null>(null)
  const [editSlogan, setEditSlogan] = useState('')

  // 删除确认
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // 上传新广告
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingUrl, setPendingUrl] = useState('')
  const [pendingSlogan, setPendingSlogan] = useState('')
  const [uploading, setUploading] = useState(false)

  const items = rawAds?.ads ?? []
  const cap = AD_CAP[slotType]
  const overCount = Math.max(0, items.length - cap)

  // 广告语上限(0 = 该广告位无广告语)
  const sloganMax = AD_SLOGAN_MAX[slotType]
  const hasSloganInput = sloganMax > 0
  const pendingSloganOver = hasSloganInput && sloganLen(pendingSlogan) > sloganMax
  const editSloganOver = hasSloganInput && sloganLen(editSlogan) > sloganMax

  // ── order mode ──────────────────────────────────────────────────────────

  const handleOrderModeChange = async (mode: 'fixed' | 'random') => {
    const prev = orderMode
    setOrderMode(mode)
    try {
      await setSlotMode.mutateAsync({ slot_type: slotType, order_mode: mode })
      toast.push('排序模式已更新', { variant: 'success' })
    } catch (e) {
      setOrderMode(prev)
      toast.push(e instanceof Error ? e.message : '更新失败', { variant: 'error' })
    }
  }

  // ── URL 编辑 ────────────────────────────────────────────────────────────

  const startEdit = (id: string, currentUrl: string) => {
    setEditingId(id)
    setEditUrl(currentUrl)
  }

  const handleSaveUrl = async (id: string) => {
    try {
      await updateAd.mutateAsync({ id, slot: slotType, target_url: editUrl })
      setEditingId(null)
      toast.push('跳转链接已更新', { variant: 'success' })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '保存失败', { variant: 'error' })
    }
  }

  // ── slogan 编辑 ─────────────────────────────────────────────────────────

  const startSloganEdit = (id: string, currentSlogan: string) => {
    setEditingSloganId(id)
    setEditSlogan(currentSlogan)
  }

  const handleSaveSlogan = async (id: string) => {
    if (editSloganOver) return
    try {
      await updateAd.mutateAsync({ id, slot: slotType, slogan: editSlogan })
      setEditingSloganId(null)
      toast.push('广告语已更新', { variant: 'success' })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '保存失败', { variant: 'error' })
    }
  }

  // ── enabled 切换 ────────────────────────────────────────────────────────

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    try {
      await updateAd.mutateAsync({ id, slot: slotType, enabled: !enabled })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '操作失败', { variant: 'error' })
    }
  }

  // ── 排序(上移/下移) ───────────────────────────────────────────────────

  const handleMove = async (index: number, dir: 'up' | 'down') => {
    const newOrder = items.map((a) => a.id)
    const swapIdx = dir === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= newOrder.length) return
    const tmp = newOrder[swapIdx]!
    newOrder[swapIdx] = newOrder[index]!
    newOrder[index] = tmp
    try {
      await reorderAds.mutateAsync({ slot_type: slotType, ordered_ids: newOrder })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '排序失败', { variant: 'error' })
    }
  }

  // ── 删除 ────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    try {
      await deleteAd.mutateAsync({ id, slot: slotType })
      setConfirmDeleteId(null)
      toast.push('广告已删除', { variant: 'success' })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '删除失败', { variant: 'error' })
    }
  }

  // ── 上传 ────────────────────────────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast.push('图片不能超过 5MB', { variant: 'error' })
      e.target.value = ''
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.push('请选择图片文件', { variant: 'error' })
      e.target.value = ''
      return
    }
    setPendingFile(file)
  }

  const handleUpload = async () => {
    if (!pendingFile || !pendingUrl.trim() || pendingSloganOver) return
    setUploading(true)
    try {
      const fileInfoId = await uploadAvatarSmall(pendingFile)
      await createAd.mutateAsync({
        slot_type: slotType,
        image_file_info_id: fileInfoId,
        target_url: pendingUrl.trim(),
        slogan: pendingSlogan,
      })
      setPendingFile(null)
      setPendingUrl('')
      setPendingSlogan('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      toast.push('广告已添加', { variant: 'success' })
    } catch (e) {
      toast.push(e instanceof Error ? e.message : '上传失败', { variant: 'error' })
    } finally {
      setUploading(false)
    }
  }

  // ── render ───────────────────────────────────────────────────────────────

  const label = AD_SLOT_LABEL[slotType]

  return (
    <AgentLayout topbarTitle={`代理 · 广告管理`}>
      <div
        className="mx-auto flex max-w-[900px] flex-col gap-6 px-6 py-8"
        data-testid={`ads-slot-${slotType}`}
      >
        {/* 标题 */}
        <header>
          <h1 className="text-xl font-bold text-gray-900">{label}</h1>
          {slotType === 'article_banner' && (
            <p className="mt-1 text-sm text-gray-500">{BANNER_HINT}</p>
          )}
        </header>

        {/* 排序模式切换 */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">排序模式</span>
            <div
              className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
              data-testid="order-mode-toggle"
            >
              {(
                [
                  { value: 'fixed', label: '固定排序' },
                  { value: 'random', label: '随机排序' },
                ] as const
              ).map(({ value, label: modeLabel }) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`order-mode-${value}`}
                  onClick={() => void handleOrderModeChange(value)}
                  disabled={setSlotMode.isPending}
                  className={[
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-60',
                    orderMode === value
                      ? 'bg-white text-amber-600 shadow-sm ring-1 ring-gray-200'
                      : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                >
                  {modeLabel}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            {orderMode === 'fixed'
              ? '按排列顺序展示，超出容量的广告不显示。'
              : '每位访客每小时随机轮换，平均曝光各广告。'}
          </p>
        </section>

        {/* 容量提示 + 超出警告 */}
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700"
            data-testid="cap-badge"
          >
            本广告位最多展示 {cap} 个
          </span>
          {overCount > 0 && orderMode === 'fixed' && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700"
              data-testid="over-count-warning"
            >
              <AlertTriangle size={12} />
              超出 {overCount} 个不会展示
            </span>
          )}
        </div>

        {/* 广告列表 */}
        <section className="rounded-xl border border-gray-200 bg-white">
          <header className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold text-gray-700">
              广告列表
              <span className="ml-2 text-xs font-normal text-gray-400">
                {items.length} 条
              </span>
            </h2>
          </header>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="text-amber-500" />
            </div>
          ) : items.length === 0 ? (
            <div
              className="flex flex-col items-center gap-2 py-12 text-sm text-gray-400"
              data-testid="empty-state"
            >
              <ImageOff size={24} />
              <p>还没有广告，点下方"上传广告"添加</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="ad-list">
              {items.map((ad, index) => {
                const isOver = orderMode === 'fixed' && index >= cap
                return (
                  <li
                    key={ad.id}
                    data-testid={`ad-row-${ad.id}`}
                    className={[
                      'flex items-center gap-3 px-5 py-3',
                      isOver ? 'opacity-50' : '',
                    ].join(' ')}
                  >
                    {/* 序号 */}
                    {orderMode === 'fixed' && (
                      <span className="w-6 shrink-0 text-center text-xs font-medium text-gray-400">
                        {index + 1}
                      </span>
                    )}

                    {/* 缩略图 */}
                    {ad.image_url ? (
                      <img
                        src={ad.image_url}
                        alt="广告图"
                        className="h-12 w-12 shrink-0 rounded object-cover ring-1 ring-gray-200"
                        data-testid={`ad-thumb-${ad.id}`}
                      />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gray-100 ring-1 ring-gray-200">
                        <ImageOff size={18} className="text-gray-300" />
                      </div>
                    )}

                    {/* URL + slogan 编辑区 */}
                    <div className="min-w-0 flex-1 flex flex-col gap-1">
                      {/* URL 编辑 */}
                      <div>
                        {editingId === ad.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="url"
                              value={editUrl}
                              onChange={(e) => setEditUrl(e.target.value)}
                              className="h-8 flex-1 rounded border border-amber-400 px-2 text-xs"
                              data-testid={`ad-url-input-${ad.id}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void handleSaveUrl(ad.id)
                                if (e.key === 'Escape') setEditingId(null)
                              }}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => void handleSaveUrl(ad.id)}
                              disabled={updateAd.isPending}
                              className="rounded bg-amber-500 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-60"
                              data-testid={`ad-url-save-${ad.id}`}
                            >
                              保存
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="rounded px-2 py-1 text-[11px] text-gray-500 hover:bg-gray-100"
                            >
                              取消
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(ad.id, ad.target_url)}
                            className="block max-w-full truncate text-left text-xs text-blue-600 hover:underline"
                            data-testid={`ad-url-${ad.id}`}
                            title={ad.target_url || '点击设置跳转链接'}
                          >
                            {ad.target_url || '点击设置跳转链接'}
                          </button>
                        )}
                      </div>

                      {/* slogan 编辑(banner 广告位不渲染) */}
                      {hasSloganInput && (
                        <div>
                          {editingSloganId === ad.id ? (
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <input
                                  type="text"
                                  value={editSlogan}
                                  onChange={(e) => setEditSlogan(e.target.value)}
                                  className={[
                                    'h-7 w-full rounded border px-2 pr-10 text-xs',
                                    editSloganOver ? 'border-red-400' : 'border-amber-400',
                                  ].join(' ')}
                                  data-testid={`ad-slogan-input-${ad.id}`}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void handleSaveSlogan(ad.id)
                                    if (e.key === 'Escape') setEditingSloganId(null)
                                  }}
                                  autoFocus
                                />
                                <span
                                  className={[
                                    'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px]',
                                    editSloganOver ? 'font-semibold text-red-500' : 'text-gray-400',
                                  ].join(' ')}
                                  data-testid={`ad-slogan-count-${ad.id}`}
                                >
                                  {sloganLen(editSlogan)}/{sloganMax}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => void handleSaveSlogan(ad.id)}
                                disabled={updateAd.isPending || editSloganOver}
                                className="rounded bg-amber-500 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-60"
                                data-testid={`ad-slogan-save-${ad.id}`}
                              >
                                保存
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingSloganId(null)}
                                className="rounded px-2 py-1 text-[11px] text-gray-500 hover:bg-gray-100"
                              >
                                取消
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startSloganEdit(ad.id, ad.slogan)}
                              className="block max-w-full truncate text-left text-[11px] text-gray-400 hover:text-amber-600"
                              data-testid={`ad-slogan-${ad.id}`}
                              title={ad.slogan || '点击设置广告语'}
                            >
                              {ad.slogan || '点击设置广告语'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 启用开关 */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={ad.enabled}
                      onClick={() => void handleToggleEnabled(ad.id, ad.enabled)}
                      disabled={updateAd.isPending}
                      data-testid={`ad-toggle-${ad.id}`}
                      className={[
                        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-60',
                        ad.enabled ? 'bg-amber-500' : 'bg-gray-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
                          ad.enabled ? 'translate-x-4' : 'translate-x-0',
                        ].join(' ')}
                      />
                    </button>

                    {/* 上移/下移 */}
                    <div className="flex shrink-0 flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => void handleMove(index, 'up')}
                        disabled={index === 0 || reorderAds.isPending}
                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                        aria-label="上移"
                        data-testid={`ad-up-${ad.id}`}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleMove(index, 'down')}
                        disabled={index >= items.length - 1 || reorderAds.isPending}
                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                        aria-label="下移"
                        data-testid={`ad-down-${ad.id}`}
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>

                    {/* 超出标签 */}
                    {isOver && (
                      <span className="shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
                        超出不展示
                      </span>
                    )}

                    {/* 删除 */}
                    {confirmDeleteId === ad.id ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => void handleDelete(ad.id)}
                          disabled={deleteAd.isPending}
                          className="rounded bg-red-500 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-60"
                          data-testid={`ad-delete-confirm-${ad.id}`}
                        >
                          确认删除
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="rounded px-2 py-1 text-[11px] text-gray-500 hover:bg-gray-100"
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(ad.id)}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        aria-label="删除广告"
                        data-testid={`ad-delete-${ad.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* 上传新广告 */}
        <section
          className="rounded-xl border border-dashed border-gray-300 bg-white p-5"
          data-testid="upload-section"
        >
          <div className="flex items-center gap-2 pb-3">
            <PlusCircle size={16} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-gray-700">上传广告</h2>
          </div>
          {/* 每项单独一行,链接/广告语输入框占满整行 */}
          <div className="flex flex-col gap-3">
            {/* 文件选择 */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">广告图片（≤ 5MB）</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="text-xs text-gray-700 file:mr-2 file:rounded file:border-0 file:bg-amber-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-amber-700"
                data-testid="upload-file-input"
              />
              {pendingFile && (
                <span className="text-[11px] text-gray-500">{pendingFile.name}</span>
              )}
            </div>

            {/* 跳转 URL */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">跳转链接</label>
              <input
                type="url"
                placeholder="https://example.com"
                value={pendingUrl}
                onChange={(e) => setPendingUrl(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-300 px-3 text-xs"
                data-testid="upload-url-input"
              />
            </div>

            {/* 广告语(banner 广告位不渲染) */}
            {hasSloganInput && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">
                  广告语（最多 {sloganMax} 字）
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={`广告语（最多 ${sloganMax} 字）`}
                    value={pendingSlogan}
                    onChange={(e) => setPendingSlogan(e.target.value)}
                    className={[
                      'h-9 w-full rounded-md border px-3 pr-14 text-xs',
                      pendingSloganOver ? 'border-red-400' : 'border-gray-300',
                    ].join(' ')}
                    data-testid="upload-slogan-input"
                  />
                  <span
                    className={[
                      'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px]',
                      pendingSloganOver ? 'font-semibold text-red-500' : 'text-gray-400',
                    ].join(' ')}
                    data-testid="upload-slogan-count"
                  >
                    {sloganLen(pendingSlogan)}/{sloganMax}
                  </span>
                </div>
                {pendingSloganOver && (
                  <span className="text-[11px] text-red-500">
                    广告语超出 {sloganLen(pendingSlogan) - sloganMax} 字，请删减后提交
                  </span>
                )}
              </div>
            )}

            {/* 提交按钮 */}
            <div>
              <button
                type="button"
                onClick={() => void handleUpload()}
                disabled={!pendingFile || !pendingUrl.trim() || uploading || pendingSloganOver}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-amber-500 px-4 text-sm font-semibold text-white disabled:opacity-60"
                data-testid="upload-submit"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
                {uploading ? '上传中…' : '添加广告'}
              </button>
            </div>
          </div>
        </section>

        {/* 翻页提示(分页由 sort_order 序列隐性实现;列表已全量展示) */}
        {items.length > 5 && (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <ChevronLeft size={14} />
            <span>共 {items.length} 条广告，已全量展示</span>
            <ChevronRight size={14} />
          </div>
        )}
      </div>
    </AgentLayout>
  )
}
